import { LogOut, User, Sun, Moon, Menu } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import NotificationBell from '@/components/notifications/NotificationBell';
import { Link } from 'react-router-dom';
import { useState, useRef, useEffect, useCallback, useLayoutEffect } from 'react';
import type { CSSProperties } from 'react';
import { createPortal } from 'react-dom';

interface HeaderProps {
  onToggleMobileNav?: () => void;
}

const MENU_MAX_WIDTH = 320;
const VIEWPORT_MARGIN = 12;
const TRIGGER_GAP = 8;

export function Header({ onToggleMobileNav }: HeaderProps) {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [menuStyle, setMenuStyle] = useState<CSSProperties>({});

  const closeDropdown = useCallback(() => setDropdownOpen(false), []);

  // Anchor the menu to the avatar button: keep the right edges aligned, but
  // clamp the box so it never leaves the viewport or overflows a parent with
  // `overflow: hidden`. The menu is rendered in a portal (see below) so it is
  // never clipped by the scrollable `.app-content-shell`.
  const positionMenu = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const width = Math.min(MENU_MAX_WIDTH, viewportWidth - VIEWPORT_MARGIN * 2);
    const left = Math.max(
      VIEWPORT_MARGIN,
      Math.min(rect.right - width, viewportWidth - VIEWPORT_MARGIN - width),
    );

    const menuHeight = dropdownRef.current?.offsetHeight ?? 0;
    let top = rect.bottom + TRIGGER_GAP;
    if (menuHeight > 0 && top + menuHeight > viewportHeight - VIEWPORT_MARGIN) {
      const above = rect.top - TRIGGER_GAP - menuHeight;
      top = above >= VIEWPORT_MARGIN
        ? above
        : Math.max(VIEWPORT_MARGIN, viewportHeight - VIEWPORT_MARGIN - menuHeight);
    }

    setMenuStyle({
      position: 'fixed',
      top: Math.round(top),
      left: Math.round(left),
      width: Math.round(width),
    });
  }, []);

  useLayoutEffect(() => {
    if (!dropdownOpen) return;
    positionMenu();
    window.addEventListener('resize', positionMenu);
    window.addEventListener('scroll', positionMenu, true);
    return () => {
      window.removeEventListener('resize', positionMenu);
      window.removeEventListener('scroll', positionMenu, true);
    };
  }, [dropdownOpen, positionMenu]);

  useEffect(() => {
    if (!dropdownOpen) return;
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || dropdownRef.current?.contains(target)) return;
      setDropdownOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDropdownOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [dropdownOpen]);

  const menu = dropdownOpen ? (
    <div
      ref={dropdownRef}
      role="menu"
      aria-label="Profile menu"
      style={menuStyle}
      className="z-[100] max-h-[calc(100dvh-1.5rem)] overflow-y-auto overscroll-contain rounded-lg border border-default bg-surface shadow-lg"
    >
      <div className="border-b border-default p-3">
        <p className="text-sm font-medium text-primary [overflow-wrap:anywhere]">{user?.name}</p>
        <p className="mt-0.5 text-xs text-secondary [overflow-wrap:anywhere]">{user?.email}</p>
      </div>
      <div className="p-1">
        <Link
          to="/profile"
          role="menuitem"
          onClick={closeDropdown}
          className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-primary hover:bg-surface-hover"
        >
          <User size={16} className="shrink-0" />
          Profile
        </Link>
        <button
          type="button"
          role="menuitem"
          onClick={() => { closeDropdown(); logout(); }}
          className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-error hover:bg-error/10"
        >
          <LogOut size={16} className="shrink-0" />
          Sign Out
        </button>
      </div>
    </div>
  ) : null;

  return (
    <header className="app-header sticky top-0 z-30 h-16 flex items-center justify-between px-4 md:px-6 gap-2 lg:gap-4">
      <button
        type="button"
        onClick={onToggleMobileNav}
        className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-lg text-secondary hover:bg-surface-hover hover:text-primary transition-colors cursor-pointer"
        aria-label="Open navigation menu"
      >
        <Menu size={20} />
      </button>

      <span className="lg:hidden min-w-0 flex-1 truncate px-2 text-sm font-semibold text-primary">
        Departmental Labour App
      </span>

      <div className="flex items-center gap-1 sm:gap-2 md:gap-4 ml-auto">
        <button
          onClick={toggleTheme}
          className="theme-toggle header-theme-toggle"
          aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
        </button>

        <NotificationBell />

        <button
          ref={triggerRef}
          type="button"
          onClick={() => setDropdownOpen((o) => !o)}
          aria-haspopup="menu"
          aria-expanded={dropdownOpen}
          className="flex items-center gap-2 rounded-lg p-1 hover:bg-surface-hover transition-colors cursor-pointer"
        >
          <div className="w-8 h-8 rounded-full bg-brand-500 flex items-center justify-center text-white text-sm font-medium">
            {user?.name.split(' ').map((n: string) => n[0]).join('').slice(0, 2)}
          </div>
          <span className="text-sm font-medium text-primary hidden sm:block">{user?.name}</span>
        </button>
      </div>

      {typeof document !== 'undefined' && createPortal(menu, document.body)}
    </header>
  );
}
